import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ModuleFilter } from "../../utils/ModuleFilter";
import { PMDialog } from "../../utils/PMDialog";
import { trainingApi } from "../../../api/training";
import { normalizeError } from "../../../operationFeedback/normalizeError";
import {
  Badge,
  buttonClass,
  primaryClass,
  inputClass,
  panelClass,
  Feedback,
  Field,
  formatDate,
  newKey,
  RemoteOptions,
  statusKeys,
  useAlive,
} from "./trainingAdminShared";

const statuses = [
  "not_started",
  "in_progress",
  "snoozed",
  "passed",
  "overdue",
  "exempt",
  "withdrawn",
  "blocked_dependency",
];
const metadataLabels = {
  reason: "trainingAdmin.reason",
  due_at: "trainingAdmin.dueAt",
  revision_number: "trainingAdmin.revision",
  card_key: "trainingAdmin.cardKey",
  passed: "trainingAdmin.correctResult",
  carried: "trainingAdmin.carried",
  source_revision_number: "trainingAdmin.sourceRevision",
  selected_option_ids: "trainingAdmin.response",
  selected_answers: "trainingAdmin.response",
  acknowledged: "trainingAdmin.acknowledged",
  change_summary: "trainingAdmin.changeNote",
  question_title: "trainingAdmin.cardTitle",
  completion_kind: "trainingAdmin.receiptKind",
};
const compactMetadata = (metadata = {}) =>
  Object.entries(metadata).filter(
    ([key]) =>
      metadataLabels[key] &&
      (key !== "selected_option_ids" || !metadata.selected_answers),
  );
const eventLabels = {
  creator_approved: 'trainingParticipant.creatorApproved',
  revision_created: "trainingAdmin.revise",
  guide_published: "trainingAdmin.publishedEvent",
  guide_reactivated: "trainingAdmin.active",
  guide_deactivated: "trainingAdmin.inactive",
  guide_archived: "trainingAdmin.archivedEvent",
  assignment_exempted: "trainingAdmin.exempt",
  guide_created: "trainingAdmin.createdEvent",
  draft_saved: "trainingAdmin.savedEvent",
  guide_copied: "trainingAdmin.copiedEvent",
  copied: "trainingAdmin.copiedEvent",
  published: "trainingAdmin.publishedEvent",
  assigned: "trainingAdmin.assignedEvent",
  reassigned: "trainingAdmin.assignedEvent",
  started: "trainingAdmin.startedEvent",
  answered: "trainingAdmin.answeredEvent",
  document_opened: "trainingAdmin.documentOpenedEvent",
  completed: "trainingAdmin.completedEvent",
  snoozed: "trainingAdmin.snoozed",
  withdrawn: "trainingAdmin.withdrawn",
  dependency_blocked: "trainingAdmin.blocked",
  exempted: "trainingAdmin.exempt",
  exempt: "trainingAdmin.exempt",
  deadline_extended: "trainingAdmin.extendedEvent",
  deactivated: "trainingAdmin.inactive",
  reactivated: "trainingAdmin.active",
  archived: "trainingAdmin.archivedEvent",
};

export function TrainingAuditTable({ rows }) {
  const { t, i18n } = useTranslation();
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="bg-slate-50 text-xs text-slate-500">
          <tr>
            <th className="p-3">{t("trainingAdmin.at")}</th>
            <th className="p-3">{t("trainingAdmin.actor")}</th>
            <th className="p-3">{t("trainingAdmin.event")}</th>
            <th className="p-3">{t("trainingAdmin.details")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-t border-slate-100 align-top">
              <td className="whitespace-nowrap p-3">
                {formatDate(row.created_at, i18n.language)}
              </td>
              <td className="p-3">{row.actor?.title || "—"}</td>
              <td className="p-3">
                {eventLabels[row.event] ? t(eventLabels[row.event]) : row.event}
              </td>
              <td className="max-w-md break-words p-3">
                {compactMetadata(row.metadata).map(([key, value]) => (
                  <p key={key}>
                    <span className="font-medium">
                      {t(metadataLabels[key])}:{" "}
                    </span>
                    {Array.isArray(value)
                      ? value.join(", ")
                      : typeof value === "boolean"
                        ? value
                          ? "✓"
                          : "✕"
                        : String(value ?? "—")}
                  </p>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!rows.length && (
        <p className="p-6 text-center text-sm text-slate-500">
          {t("trainingAdmin.noResults")}
        </p>
      )}
    </div>
  );
}

function AssignmentHistory({ assignment, onClose, enabled, onChanged }) {
  const { t, i18n } = useTranslation();
  const [record, setRecord] = useState(assignment);
  const [events, setEvents] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [offset, setOffset] = useState(0);
  const [count, setCount] = useState(0);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState(null);
  const [reason, setReason] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [version, setVersion] = useState(0);
  const pending = useRef(null);
  const busyRef = useRef(false);
  const alive = useAlive();
  useEffect(() => {
    let current = true;
    Promise.all([
      trainingApi.assignmentAudit(assignment.id, { offset, limit: 50 }),
      trainingApi.assignmentEvidence(assignment.id, { offset, limit: 50 }),
    ])
      .then(([result, evidence]) => {
        if (current) {
          setEvents(result.data || []);
          setCount(Math.max(result.count || 0, evidence.count || 0));
          setReceipts(evidence.data || []);
        }
      })
      .catch((e) => {
        if (current) setError(normalizeError(e));
      });
    return () => {
      current = false;
    };
  }, [assignment.id, offset, version]);
  const perform = async (request) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    setMessage(null);
    pending.current = request;
    try {
      const result = await request.run();
      if (!alive.current) return;
      setRecord((previous) => ({ ...previous, ...result.data }));
      pending.current = null;
      setMessage(t("trainingAdmin.updated"));
      setAction(null);
      setVersion((old) => old + 1);
      try {
        await onChanged();
      } catch {
        if (alive.current)
          setMessage(
            `${t("trainingAdmin.updated")}. ${t("trainingAdmin.refreshWarning")}`,
          );
      }
    } catch (e) {
      if (alive.current) {
        const failure = normalizeError(e);
        if (failure.httpStatus >= 400 && failure.httpStatus < 500) {
          pending.current = null;
        }
        setError(failure);
      }
    } finally {
      busyRef.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const submit = () => {
    if (busyRef.current || pending.current) return;
    if (!reason.trim()) {
      setError({
        safeMessage: t("trainingAdmin.validationReason"),
        httpStatus: 422,
      });
      return;
    }
    const payload = {
      reason: reason.trim(),
      request_key: newKey(),
      ...(action === "extend" ? { due_at: new Date(dueAt).toISOString() } : {}),
    };
    perform({
      run: () =>
        action === "exempt"
          ? trainingApi.exempt(assignment.id, payload)
          : trainingApi.extendDeadline(assignment.id, payload),
    });
  };
  return (
    <PMDialog
      open
      onClose={() => {
        if (!busyRef.current && !pending.current) onClose();
      }}
      title={`${assignment.user?.title || ""} · ${assignment.title || assignment.guide?.title}`}
      size="5xl"
      closeOnOutsideClick
    >
      <div className="space-y-5">
        <Feedback error={error} message={message}>
          {pending.current && !busy && (
            <button
              className={`${buttonClass} mt-3`}
              disabled={busy}
              onClick={() => perform(pending.current)}
            >
              {t("trainingAdmin.checkStatus")}
            </button>
          )}
        </Feedback>
        <div className="flex flex-wrap items-center gap-3">
          <Badge status={record.status} />
          <span className="text-sm text-slate-500">
            {t("trainingAdmin.revision")} {assignment.revision_number} ·{" "}
            {t("trainingAdmin.dueAt")}:{" "}
            {formatDate(record.due_at, i18n.language)}
          </span>
        </div>
        {enabled &&
          !["passed", "completed", "exempt", "withdrawn"].includes(
            record.status,
          ) &&
          !action &&
          !pending.current && (
            <div className="flex flex-wrap gap-2">
              <button
                className={buttonClass}
                onClick={() => setAction("extend")}
              >
                {t("trainingAdmin.extendDeadline")}
              </button>
              <button
                className={buttonClass}
                onClick={() => setAction("exempt")}
              >
                {t("trainingAdmin.giveExemption")}
              </button>
            </div>
          )}
        {action && (
          <fieldset
            disabled={busy || Boolean(pending.current)}
            className={`${panelClass} space-y-4`}
          >
            <h3 className="font-bold">
              {t(
                action === "exempt"
                  ? "trainingAdmin.giveExemption"
                  : "trainingAdmin.extendDeadline",
              )}
            </h3>
            {action === "exempt" && (
              <p className="text-sm text-slate-600">
                {t("trainingAdmin.exemptionInfo")}
              </p>
            )}
            {action === "extend" && (
              <Field label={t("trainingAdmin.dueAt")}>
                <input
                  className={inputClass}
                  type="datetime-local"
                  value={dueAt}
                  onChange={(event) => setDueAt(event.target.value)}
                />
              </Field>
            )}
            <Field label={t("trainingAdmin.reason")}>
              <textarea
                className={inputClass}
                value={reason}
                rows={3}
                maxLength={2000}
                onChange={(event) => setReason(event.target.value)}
              />
            </Field>
            <div className="flex flex-wrap gap-2">
              <button className={buttonClass} onClick={() => setAction(null)}>
                {t("trainingAdmin.cancel")}
              </button>
              <button
                className={primaryClass}
                onClick={submit}
                disabled={
                  !reason.trim() ||
                  (action === "extend" &&
                    (!dueAt || new Date(dueAt) <= new Date()))
                }
              >
                {t("trainingAdmin.confirm")}
              </button>
            </div>
          </fieldset>
        )}
        <div className="overflow-hidden rounded-xl border border-slate-200">
          <TrainingAuditTable rows={events} />
        </div>
        {receipts.map((receipt) => (
          <details key={receipt.id} className={panelClass}>
            <summary className="cursor-pointer font-semibold">
              {t("trainingAdmin.receipt")} · {t("trainingAdmin.revision")}{" "}
              {receipt.revision_number} ·{" "}
              {formatDate(receipt.completed_at, i18n.language)}
            </summary>
            <div className="mt-4 space-y-3">
              {receipt.kind === "creator_approved" && <p className="text-sm text-green-800">{t("trainingParticipant.creatorApprovalStatement")}</p>}
              {Object.entries(receipt.evidence?.cards || {}).map(
                ([key, card]) => (
                  <div className="rounded-xl bg-slate-50 p-3 text-sm" key={key}>
                    <h4 className="font-semibold">
                      {card.question_title ||
                        `${t("trainingAdmin.cardKey")}: ${key}`}
                    </h4>
                    <p>
                      {t("trainingAdmin.at")}:{" "}
                      {formatDate(card.answered_at, i18n.language)}
                    </p>
                    {card.source_response_id && (
                      <p>
                        {t("trainingAdmin.carried")} ·{" "}
                        {t("trainingAdmin.sourceRevision")}:{" "}
                        {card.source_revision_number || card.revision_id}
                      </p>
                    )}
                    {card.selected_answers?.length > 0 ? (
                      <p>
                        {t("trainingAdmin.response")}:{" "}
                        {card.selected_answers.join(", ")}
                      </p>
                    ) : card.answer?.option_ids?.length > 0 ? (
                      <p>
                        {t("trainingAdmin.response")}:{" "}
                        {card.answer.option_ids.join(", ")}
                      </p>
                    ) : null}
                    {card.answer?.acknowledged && (
                      <p>✓ {t("trainingAdmin.acknowledged")}</p>
                    )}
                  </div>
                ),
              )}
            </div>
          </details>
        ))}
        <div className="flex justify-end gap-2">
          <button
            className={buttonClass}
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - 50))}
          >
            {t("trainingAdmin.previous")}
          </button>
          <button
            className={buttonClass}
            disabled={offset + 50 >= count}
            onClick={() => setOffset(offset + 50)}
          >
            {t("trainingAdmin.next")}
          </button>
        </div>
      </div>
    </PMDialog>
  );
}

export default function TrainingAdminReports({
  enabled = true,
  auditOnly = false,
  initialGuideId = null,
  responsibleOnly = false,
}) {
  const { t, i18n } = useTranslation();
  const [filters, setFilters] = useState({ query: "", statuses: [], guides: initialGuideId ? [initialGuideId] : [] });
  const [revisionNumber, setRevisionNumber] = useState("");
  const [dueBefore, setDueBefore] = useState("");
  const [userId, setUserId] = useState(null);
  const [mode, setMode] = useState("guide");
  const [rows, setRows] = useState([]);
  const [guides, setGuides] = useState([]);
  const [offset, setOffset] = useState(0);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [version, setVersion] = useState(0);
  const [selected, setSelected] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [peopleOpen, setPeopleOpen] = useState(false);
  const alive = useAlive();
  const params = {
    revision_number: revisionNumber || undefined,
    due_before: dueBefore
      ? new Date(`${dueBefore}T23:59:59`).toISOString()
      : undefined,
    sort_by: mode,
    query: auditOnly ? undefined : filters.query,
    status: filters.statuses?.join(","),
    guide_id: filters.guides?.length ? filters.guides : undefined,
    user_id: userId || undefined,
    offset,
    limit: 50,
  };
  const paramsKey = JSON.stringify(params);
  useEffect(() => {
    let current = true;
    trainingApi
      .reportGuides({ limit: 100 })
      .then((result) => {
        if (current) setGuides(result.data || []);
      })
      .catch((e) => {
        if (current) setError(normalizeError(e));
      });
    return () => {
      current = false;
    };
  }, []);
  useEffect(() => {
    let current = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const result = await (auditOnly
          ? trainingApi.audit(JSON.parse(paramsKey))
          : trainingApi.reports(JSON.parse(paramsKey)));
        if (current) {
          setRows(result.data || []);
          setCount(result.count || 0);
          setError(null);
        }
      } catch (e) {
        if (current) {
          setError(normalizeError(e));
          setRows([]);
        }
      } finally {
        if (current) setLoading(false);
      }
    }, 250);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [paramsKey, auditOnly, version]);
  const exportFile = async (format) => {
    if (exporting) return;
    setExporting(true);
    setError(null);
    try {
      const blob = await trainingApi.exportReport({
        ...params,
        offset: undefined,
        limit: undefined,
        format,
      });
      if (!alive.current) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `training-report.${format}`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      if (alive.current) setError(normalizeError(e));
    } finally {
      if (alive.current) setExporting(false);
    }
  };
  const filterChanged = (next) => {
    setFilters(next);
    setOffset(0);
  };
  return (
    <div className="space-y-5">
      {!enabled && !responsibleOnly && (
        <p className="rounded-xl bg-amber-50 p-4 text-sm text-amber-900">
          {t("trainingAdmin.readOnly")}
        </p>
      )}
      <div className={`${auditOnly ? "flex flex-wrap items-start gap-3" : "space-y-4"} rounded-[18px] border border-slate-200 bg-white p-4 shadow-sm`}>
      <ModuleFilter
        hideSearch={auditOnly}
        hideStatusFilter={auditOnly}
        customDropdownFilters={[{
          key: "guides",
          label: "trainingAdmin.guide",
          options: guides.map((guide) => ({ value: guide.id, label: guide.title })),
        }]}
        segmentedControl={auditOnly ? undefined : {
          value: mode,
          options: [
            { value: "guide", label: t("trainingAdmin.perGuide") },
            { value: "user", label: t("trainingAdmin.perUser") },
          ],
          onChange: (value) => { setMode(value); setOffset(0); },
        }}
        statusOptions={statuses.map((value) => ({
          value,
          label: t(statusKeys[value]),
        }))}
        initialFilters={filters}
        onFiltersChange={filterChanged}
      />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          {!auditOnly && (
            <>
              <Field label={t("trainingAdmin.revision")}>
                <input
                  className={`${inputClass} !w-24`}
                  type="number"
                  min="1"
                  value={revisionNumber}
                  onChange={(event) => {
                    setRevisionNumber(event.target.value);
                    setOffset(0);
                  }}
                />
              </Field>
              <Field label={t("trainingAdmin.dueBefore")}>
                <input
                  className={inputClass}
                  type="date"
                  value={dueBefore}
                  onChange={(event) => {
                    setDueBefore(event.target.value);
                    setOffset(0);
                  }}
                />
              </Field>
            </>
          )}
          {!responsibleOnly && (
            <button className={`${buttonClass} !h-[46px] !rounded-full !px-[18px]`} onClick={() => setPeopleOpen(true)}>
              {t("trainingAdmin.user")}
              {userId ? ` #${userId}` : ""}
            </button>
          )}
          {userId && (
            <button
              className={buttonClass}
              onClick={() => {
                setUserId(null);
                setOffset(0);
              }}
            >
              {t("trainingAdmin.remove")}
            </button>
          )}
        </div>
        {!auditOnly && (
          <div className="flex flex-wrap gap-2">
            <button
              className={buttonClass}
              disabled={exporting}
              onClick={() => exportFile("csv")}
            >
              {t("trainingAdmin.exportCsv")}
            </button>
            <button
              className={buttonClass}
              disabled={exporting}
              onClick={() => exportFile("pdf")}
            >
              {t("trainingAdmin.exportPdf")}
            </button>
          </div>
        )}
      </div>
      </div>
      <Feedback error={error}>
        {error && (
          <button
            className={`${buttonClass} mt-3`}
            onClick={() => setVersion((value) => value + 1)}
          >
            {t("trainingAdmin.reload")}
          </button>
        )}
      </Feedback>
      <div
        className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
        aria-busy={loading}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-slate-900">{t(auditOnly ? "trainingAdmin.audit" : "trainingAdmin.reports")}</h2>
          <span role="status" className="text-sm tabular-nums text-slate-500">{loading ? t("Laster...") : count}</span>
        </div>
        {auditOnly ? (
          <TrainingAuditTable rows={rows} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500">
                <tr>
                  {[
                    mode === "guide"
                      ? "trainingAdmin.guide"
                      : "trainingAdmin.user",
                    mode === "guide"
                      ? "trainingAdmin.user"
                      : "trainingAdmin.guide",
                    "trainingAdmin.status",
                    "trainingAdmin.progress",
                    "trainingAdmin.dueAt",
                    "trainingAdmin.actions",
                  ].map((key) => (
                    <th key={key} className="px-4 py-3">
                      {t(key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-t border-slate-100 hover:bg-slate-50"
                  >
                    <td className="px-4 py-4 font-semibold text-slate-900">
                      {mode === "guide"
                        ? row.title || row.guide?.title
                        : row.user?.title}
                      <div className="mt-1 text-xs font-normal text-slate-500">
                        {t("trainingAdmin.revision")} {row.revision_number}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      {mode === "guide"
                        ? row.user?.title
                        : row.title || row.guide?.title}
                    </td>
                    <td className="px-4 py-4">
                      {row.completion_kind === 'creator_approved' ? <span className="rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-800">{t('trainingParticipant.creatorApproved')}</span> : <Badge status={row.status} />}
                    </td>
                    <td className="px-4 py-4 tabular-nums">
                      {row.completion_kind === 'creator_approved' ? '—' : `${row.progress?.passed || 0} / ${row.progress?.total || 0}`}
                      {row.progress?.carried > 0 && (
                        <span className="block text-xs text-slate-500">
                          {row.progress.carried} {t("trainingAdmin.carried")}
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-4">
                      {formatDate(row.due_at, i18n.language)}
                    </td>
                    <td className="px-4 py-4">
                      {!responsibleOnly && (
                        <button
                          className={buttonClass}
                          onClick={() => setSelected(row)}
                        >
                          {t("trainingAdmin.details")}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!loading && !rows.length && (
              <p className="p-10 text-center text-sm text-slate-500">
                {t("trainingAdmin.noResults")}
              </p>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2 border-t border-slate-100 p-3">
          <button
            className={buttonClass}
            disabled={offset === 0 || loading}
            onClick={() => setOffset(Math.max(0, offset - 50))}
          >
            {t("trainingAdmin.previous")}
          </button>
          <button
            className={buttonClass}
            disabled={offset + 50 >= count || loading}
            onClick={() => setOffset(offset + 50)}
          >
            {t("trainingAdmin.next")}
          </button>
        </div>
      </div>
      {selected && (
        <AssignmentHistory
          key={selected.id}
          assignment={selected}
          enabled={enabled}
          onClose={() => setSelected(null)}
          onChanged={async () => {
            setVersion((value) => value + 1);
          }}
        />
      )}
      {peopleOpen && (
        <PMDialog
          open
          title={t("trainingAdmin.user")}
          onClose={() => setPeopleOpen(false)}
          closeOnOutsideClick
        >
          <RemoteOptions
            kind="users"
            label={t("trainingAdmin.user")}
            multiple={false}
            selected={userId ? [userId] : []}
            onChange={(ids) => {
              setUserId(ids[0]);
              setOffset(0);
              setPeopleOpen(false);
            }}
          />
        </PMDialog>
      )}
    </div>
  );
}
